import React, { useState } from "react";
import Header from "../../components/layout/Header";
import BottomBar from "../../components/staxs/BottomBar";
import { HiMagnifyingGlass, HiFunnel } from "react-icons/hi2";

const ExplorePage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"watchlist" | "trending">(
    "trending"
  );

  // Dummy data for trending sports moments
  const trendingMoments = [
    {
      id: 1,
      title: "Curry 3-Pointer Record",
      sport: "Basketball",
      player: "Stephen Curry",
      team: "Golden State Warriors",
      price: 2400.0,
      change24h: 15.3,
      rarity: "Legendary",
      image: "BB",
      volume: "1.2K",
    },
    {
      id: 2,
      title: "Ronaldo Bicycle Kick",
      sport: "Soccer",
      player: "Cristiano Ronaldo",
      team: "Al Nassr",
      price: 1850.0,
      change24h: -3.2,
      rarity: "Epic",
      image: "SC",
      volume: "890",
    },
    {
      id: 3,
      title: "Judge Home Run Derby",
      sport: "Baseball",
      player: "Aaron Judge",
      team: "New York Yankees",
      price: 1200.0,
      change24h: 8.7,
      rarity: "Rare",
      image: "BS",
      volume: "654",
    },
    {
      id: 4,
      title: "Verstappen Pole Position",
      sport: "Formula 1",
      player: "Max Verstappen",
      team: "Red Bull Racing",
      price: 950.0,
      change24h: 12.1,
      rarity: "Epic",
      image: "RC",
      volume: "432",
    },
  ];

  // Dummy data for watchlist
  const watchlistMoments = [
    {
      id: 1,
      title: "Mahomes Championship Pass",
      sport: "Football",
      player: "Patrick Mahomes",
      team: "Kansas City Chiefs",
      price: 3200.0,
      change24h: 22.5,
      rarity: "Legendary",
      image: "FB",
      volume: "2.1K",
    },
    {
      id: 2,
      title: "Messi World Cup Goal",
      sport: "Soccer",
      player: "Lionel Messi",
      team: "Argentina",
      price: 4500.0,
      change24h: -5.8,
      rarity: "Legendary",
      image: "SC",
      volume: "3.4K",
    },
    {
      id: 3,
      title: "Ohtani Perfect Game",
      sport: "Baseball",
      player: "Shohei Ohtani",
      team: "Los Angeles Angels",
      price: 2800.0,
      change24h: 18.9,
      rarity: "Epic",
      image: "BS",
      volume: "1.8K",
    },
  ];

  const categories = [
    { name: "All", icon: "ALL", active: true },
    { name: "Basketball", icon: "BB", active: false },
    { name: "Soccer", icon: "SC", active: false },
    { name: "Football", icon: "FB", active: false },
    { name: "Baseball", icon: "BS", active: false },
    { name: "Hockey", icon: "HK", active: false },
  ];

  const getRarityColor = (rarity: string) => {
    switch (rarity.toLowerCase()) {
      case "legendary":
        return "bg-gradient-to-r from-yellow-400 to-orange-500 text-white";
      case "epic":
        return "bg-gradient-to-r from-purple-500 to-pink-500 text-white";
      case "rare":
        return "bg-gradient-to-r from-blue-500 to-cyan-500 text-white";
      default:
        return "bg-base-2000 text-white";
    }
  };

  return (
    <div className="w-full pt-16 min-h-screen">
      <Header />
      <div className="container max-w-2xl mx-auto py-8 pb-0 px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-base-content font-grotesk">
            Explore
          </h1>
          <button className="p-2 hover:bg-base-300 rounded-lg transition-colors">
            <HiFunnel className="w-6 h-6 text-base-content/70" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-6">
          <HiMagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-base-content/50" />
          <input
            type="text"
            placeholder="Search sports moments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-base-100 border border-base-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Categories */}
        <div
          className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide cursor-grab active:cursor-grabbing"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {categories.map((category) => (
            <button
              key={category.name}
              className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                category.active
                  ? "bg-neutral text-neutral-content"
                  : "bg-base-100 text-base-content/70 hover:bg-base-200"
              }`}
            >
              <span className="text-sm font-medium">{category.name}</span>
            </button>
          ))}
        </div>

        {/* Tab Navigation */}
        <div className="bg-base-100 rounded-2xl shadow-sm mb-6">
          <div className="p-6 border-b border-base-200">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab("trending")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === "trending"
                    ? "bg-neutral text-neutral-content"
                    : "bg-base-200 text-base-content/70 hover:bg-base-300"
                }`}
              >
                Trending
              </button>
              <button
                onClick={() => setActiveTab("watchlist")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === "watchlist"
                    ? "bg-neutral text-neutral-content"
                    : "bg-base-200 text-base-content/70 hover:bg-base-300"
                }`}
              >
                Watchlist
              </button>
            </div>
          </div>

          {/* Trending Tab */}
          {activeTab === "trending" && (
            <div className="divide-y divide-base-200">
              {trendingMoments.map((moment) => (
                <div
                  key={moment.id}
                  className="p-4 hover:bg-base-200 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-base-200 rounded-lg flex items-center justify-center text-2xl">
                        {moment.image}
                      </div>
                      <div>
                        <h4 className="font-semibold text-base-content text-sm">
                          {moment.title}
                        </h4>
                        <p className="text-xs text-base-content/60">{moment.player}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className={`text-xs px-2 py-1 rounded-full font-medium ${getRarityColor(
                              moment.rarity
                            )}`}
                          >
                            {moment.rarity}
                          </span>
                          <span className="text-xs text-base-content/50">
                            Vol: {moment.volume}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-base-content">
                        ${moment.price.toLocaleString()}
                      </p>
                      <p
                        className={`text-sm ${
                          moment.change24h >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {moment.change24h >= 0 ? "+" : ""}
                        {moment.change24h}%
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Watchlist Tab */}
          {activeTab === "watchlist" && (
            <div className="divide-y divide-base-200">
              {watchlistMoments.map((moment) => (
                <div
                  key={moment.id}
                  className="p-4 hover:bg-base-200 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-base-200 rounded-lg flex items-center justify-center text-2xl">
                        {moment.image}
                      </div>
                      <div>
                        <h4 className="font-semibold text-base-content text-sm">
                          {moment.title}
                        </h4>
                        <p className="text-xs text-base-content/60">{moment.player}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className={`text-xs px-2 py-1 rounded-full font-medium ${getRarityColor(
                              moment.rarity
                            )}`}
                          >
                            {moment.rarity}
                          </span>
                          <span className="text-xs text-base-content/50">
                            Vol: {moment.volume}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-base-content">
                        ${moment.price.toLocaleString()}
                      </p>
                      <p
                        className={`text-sm ${
                          moment.change24h >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {moment.change24h >= 0 ? "+" : ""}
                        {moment.change24h}%
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom spacing for fixed bottom bar */}
        <div className="h-24"></div>
      </div>

      {/* STAX Bottom Navigation Bar */}
      <BottomBar />
    </div>
  );
};

export default ExplorePage;
